import { type ReactElement } from 'react';

/**
 * Brand glyphs (24×24, currentColor, even-odd fill) and their brand colours, for the round share buttons of
 * `ShareStoreCard`. Inline: no third-party script, image or font is loaded for them.
 */
export type BrandIconName =
  'whatsapp' | 'facebook' | 'telegram' | 'instagram' | 'tiktok' | 'youtube';

const PATHS: Readonly<Record<BrandIconName, string>> = {
  facebook:
    'M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.5-1.5h1.6V4.4c-.3 0-1.2-.1-2.3-.1-2.3 0-3.8 1.4-3.8 3.9v2.3H8v3h2.5V21h3z',
  instagram:
    'M8 3h8a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8a5 5 0 0 1 5-5zM8.4 4.8h7.2a3.6 3.6 0 0 1 3.6 3.6v7.2a3.6 3.6 0 0 1-3.6 3.6H8.4a3.6 3.6 0 0 1-3.6-3.6V8.4a3.6 3.6 0 0 1 3.6-3.6zM12 7.8a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 0 0 0-8.4zM12 9.6a2.4 2.4 0 1 1 0 4.8 2.4 2.4 0 0 1 0-4.8zM17.3 5.6a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2z',
  tiktok:
    'M16.6 5.8A4.3 4.3 0 0 1 15.5 3h-3v12.1a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .8.1V9.5a5.6 5.6 0 1 0 4.8 5.6V9a7.2 7.2 0 0 0 4.2 1.3v-3a4.3 4.3 0 0 1-3.1-1.5z',
  youtube:
    'M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8zM10 15V9l5.2 3L10 15z',
  whatsapp:
    'M12 3a9 9 0 0 0-7.8 13.5L3 21l4.6-1.2A9 9 0 1 0 12 3zm0 16.4c-1.4 0-2.8-.4-4-1.1l-.3-.2-2.7.7.7-2.6-.2-.3A7.4 7.4 0 1 1 12 19.4zm4.1-5.5c-.2-.1-1.3-.7-1.5-.7-.2-.1-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6 6 0 0 1-3-2.6c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.7-1.6c-.2-.4-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3 2.9 2.9 0 0 0-.9 2.2c0 1.3.9 2.5 1 2.7.1.2 1.8 2.8 4.4 3.9 1.6.7 2.3.8 3.1.6.5-.1 1.3-.6 1.5-1.1.2-.5.2-1 .1-1.1l-.2-.1z',
  telegram:
    'M21.5 4.3 18.3 19.6c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-5 9.1-8.2c.4-.4-.1-.6-.6-.2L5.8 13.3 1 11.8c-1-.3-1-1 .2-1.5L20.2 3c.9-.3 1.6.2 1.3 1.3z',
};

/** Brand colour of each round button (Instagram's gradient as a Tailwind class). */
export const BRAND_BG: Readonly<Record<BrandIconName, string>> = {
  whatsapp: 'bg-[#25D366]',
  facebook: 'bg-[#1877F2]',
  telegram: 'bg-[#229ED9]',
  instagram: 'bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
  tiktok: 'bg-[#111111]',
  youtube: 'bg-[#FF0000]',
};

export function BrandIcon({
  name,
  className = 'h-[18px] w-[18px]',
}: {
  name: BrandIconName;
  className?: string;
}): ReactElement {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="currentColor">
      <path fillRule="evenodd" clipRule="evenodd" d={PATHS[name]} />
    </svg>
  );
}
